import { createElement, useRef, useState } from 'react';
import styled from 'styled-components';
import type { MenuProps } from '../ui';
import { Skeleton } from '../ui';
import { normalizeContextItems } from './normalize-context-items';
import type { NodeWithMenu, GetContextMenuItems } from './types';

export const LoadingMenuItem = styled(Skeleton.Button)`
  .ant-skeleton-button {
    height: 18px;
    line-height: 18px;
  }
`;

export const useContextMenu = <TNode extends NodeWithMenu>(
  defaultItems: GetContextMenuItems<TNode>,
  onClick: (k: string, node?: TNode) => void,
) => {
  const [menuProps, updateMenuProps] = useState<MenuProps>({
    items: [],
  });

  const openDefaultMenu = useRef<boolean>(true);
  const cancelRef = useRef<(() => void) | null>(null);

  const openMenuItems = (items: GetContextMenuItems<TNode>, node?: TNode) => {
    if (cancelRef.current) {
      cancelRef.current();
    }

    const { promise, cancel } = normalizeContextItems(items, node);
    cancelRef.current = cancel;

    // Mock menu while promise not resolved
    updateMenuProps({
      ...menuProps,
      items: Array.from({ length: 5 }, (_, i) => ({
        key: `loading-${i}`,
        disabled: true,
        label: createElement(LoadingMenuItem, { active: true }),
      })),
    });

    promise.then(items => {
      updateMenuProps(prev => {
        cancelRef.current = null;

        return {
          ...prev,
          items,
          onClick: e => {
            onClick(e.key, node);
          },
        };
      });
    });
  };

  const createMenu = (node: TNode) => {
    openDefaultMenu.current = false; // prevent open default context menu items
    openMenuItems(node.getContextMenuItems?.call(node), node);
  };

  const beforeOpenMenu = () => {
    if (openDefaultMenu.current) {
      openMenuItems(defaultItems);
    }

    setTimeout(() => {
      openDefaultMenu.current = true;
    });
  };

  return {
    menuProps,
    createMenu,
    beforeOpenMenu,
  };
};
